import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Building2, MapPin, Users, Award, TrendingUp, Shield, ChevronLeft, ChevronRight, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { useSiteContent } from '@/lib/useSiteContent';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const services = [
  {
    icon: MapPin,
    title: 'Land Subdivision',
    description: 'Transforming raw land into well-planned residential and commercial sections ready for development.'
  },
  {
    icon: Building2,
    title: 'Residential Development',
    description: 'Building quality homes designed for modern living with sustainable practices and premium finishes.'
  },
  {
    icon: HeartHandshake,
    title: 'Social Housing',
    description: 'Delivering accessible, affordable homes in partnership with local government to strengthen Manawatū communities and support long-term growth.'
  }
];

const whyChooseUs = [
  { icon: Award, title: 'Industry Experience', description: 'Years of expertise in Manawatu property development' },
  { icon: Users, title: 'Community Focus', description: 'Building developments that strengthen local communities' },
  { icon: Shield, title: 'Quality Assurance', description: 'Commitment to excellence in every project we undertake' },
  { icon: TrendingUp, title: 'Long-term Vision', description: 'Creating sustainable growth for future generations' }
];

export default function HomePage() {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { tx } = useSiteContent();
  
  // Fetch hero slides
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await publicApi.getHeroSlides();
        setHeroSlides(response.data);
      } catch (error) {
        console.error('Error fetching hero slides:', error);
        // Fallback slides
        setHeroSlides([
          { id: '1', url: 'https://images.unsplash.com/photo-1702970837084-5b65897c8c3b?w=1600', alt: 'Manawatu' },
          { id: '2', url: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600', alt: 'Modern Home' },
        ]);
      }
    };
    fetchSlides();
  }, []);
  
  // Auto-advance carousel
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, [heroSlides.length]);
  
  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, [heroSlides.length]);
  
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        await publicApi.seedData();
        const response = await publicApi.getProjects({ featured: true });
        setFeaturedProjects(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, []);
  
  return (
    <div className="relative" data-testid="home-page">
      {/* Hero Section with Carousel */}
      <section className="relative h-screen min-h-[700px] flex items-center" data-testid="hero-section">
        {/* Carousel Background */}
        <div className="absolute inset-0 overflow-hidden">
          <AnimatePresence mode="wait">
            {heroSlides.length > 0 && (
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                <img 
                  src={heroSlides[currentSlide]?.url} 
                  alt={heroSlides[currentSlide]?.alt || 'Hero image'}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        {/* Carousel Navigation Arrows */}
        {heroSlides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </>
        )}
        
        {/* Carousel Dots */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide 
                    ? 'bg-white w-8' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
        
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 w-full">
          <motion.div 
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-white/70 text-sm tracking-[0.2em] uppercase mb-6"
            >
              {tx('home.hero.eyebrow', 'Property & Land Developers')}
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1] mb-6"
            >
              {tx('home.hero.heading_line1', "Palmerston North's Leading")}
              <br />
              {tx('home.hero.heading_line2', 'Multi-Unit Developer.')}
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-white/80 text-2xl md:text-3xl leading-relaxed mb-10 max-w-xl"
            >
              {tx('home.hero.subtext', 'Making Premium Living Affordable.')}
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Button 
                asChild 
                size="lg" 
                className="rounded-full px-8 py-6 bg-white text-black hover:bg-white/90 transition-transform hover:scale-105"
                data-testid="hero-cta-projects"
              >
                <Link to="/projects">
                  {tx('home.hero.cta_primary', 'View Our Projects')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="rounded-full px-8 py-6 border-white/30 text-white hover:bg-white/10 bg-transparent"
                data-testid="hero-cta-contact"
              >
                <Link to="/contact">Get In Touch</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div 
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-white/50 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>
      
      {/* Who We Are Section */}
      <section className="py-20 md:py-32" data-testid="who-we-are-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center"
          >
            <div className="md:col-span-5">
              <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
                {tx('home.who.eyebrow', 'Who We Are')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                {tx('home.who.heading', 'Building for a Better Future')}
              </h2>
            </div>
            
            <div className="md:col-span-7">
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                {tx('home.who.p1', "Shaping Manawatū's urban future through over a decade of experience and 100+ properties delivered—building high-quality, accessible, and affordable communities.")}
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {tx('home.who.p2', 'With a commitment to excellence and community-focused values, we work closely with the local government to create developments that support long-term growth and enhance the quality of life for Manawatu residents.')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* What We Do Section */}
      <section className="py-20 md:py-32 bg-secondary/50" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
              {tx('home.services.eyebrow', 'What We Do')}
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              {tx('home.services.heading', 'Our Services')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: MapPin, titleKey: 'home.services.subdivisions.title', defaultTitle: 'Land Subdivision', descKey: 'home.services.subdivisions.desc', defaultDesc: 'Transforming raw land into well-planned residential and commercial sections ready for development.' },
              { icon: Building2, titleKey: 'home.services.residential.title', defaultTitle: 'Residential Development', descKey: 'home.services.residential.desc', defaultDesc: 'Building quality homes designed for modern living with sustainable practices and premium finishes.' },
              { icon: HeartHandshake, titleKey: 'home.services.social_housing.title', defaultTitle: 'Social Housing', descKey: 'home.services.social_housing.desc', defaultDesc: 'Delivering accessible, affordable homes in partnership with local government to strengthen Manawatū communities and support long-term growth.' }
            ].map((service, index) => (
              <motion.div
                key={service.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-border/40 p-8 md:p-10 hover:border-black/20 transition-colors duration-300"
              >
                <service.icon className="w-10 h-10 text-foreground mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-semibold mb-4">{tx(service.titleKey, service.defaultTitle)}</h3>
                <p className="text-muted-foreground leading-relaxed">{tx(service.descKey, service.defaultDesc)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Projects Section */}
      <section className="py-20 md:py-32" data-testid="featured-projects-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6"
          >
            <div>
              <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
                Our Work
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
                Featured Projects
              </h2>
            </div>
            <Button asChild variant="outline" className="rounded-full self-start md:self-auto" data-testid="view-all-projects">
              <Link to="/projects">
                View All Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-96 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${index === 0 ? 'md:col-span-8' : 'md:col-span-4'} group`}
                >
                  <Link to={`/projects/${project.id}`} className="block" data-testid={`featured-project-${index}`}>
                    <div className="relative overflow-hidden aspect-[4/3] bg-secondary">
                      <img 
                        src={project.images?.[project.images.length - 1]?.url || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'} 
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                        <span className={`inline-block px-3 py-1 text-xs uppercase tracking-wider rounded-full mb-3 ${
                          project.status === 'completed' ? 'bg-green-500/20 text-green-100' :
                          project.status === 'ongoing' ? 'bg-amber-500/20 text-amber-100' :
                          'bg-blue-500/20 text-blue-100'
                        }`}>
                          {project.status}
                        </span>
                        <h3 className="text-white text-xl md:text-2xl font-semibold">{project.name}</h3>
                        <p className="text-white/70 text-sm mt-1">{project.location}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Why Choose Us Section */}
      <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="why-choose-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-primary-foreground/60 text-sm tracking-[0.2em] uppercase mb-4">
              Why Choose Us
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
              Built on Trust & Excellence
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-primary-foreground/20 rounded-full flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-primary-foreground/80" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                <p className="text-primary-foreground/60 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Community Impact Section */}
      <section className="py-20 md:py-32" data-testid="community-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
                {tx('home.community.eyebrow', 'Community Impact')}
              </p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                {tx('home.community.heading', 'Growing Together with Manawatu')}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {tx('home.community.p1', "Our developments are more than just buildings and land—they're foundations for thriving communities. We work closely with local councils, businesses, and residents to ensure our projects contribute positively to Manawatu's growth and prosperity.")}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                {tx('home.community.p2', "From quality family homes to thriving commercial spaces, our projects enhance the fabric of Manawatu, creating opportunities for generations to come.")}
              </p>
              <Button asChild className="rounded-full" data-testid="learn-more-cta">
                <Link to="/about">
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="image-zoom aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_maple-land-dev/artifacts/o6sx4chh_Mlson%20Line%20aerial-10.jpg"
                  alt="Koro Living multi-unit development in Manawatu"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-secondary/50" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              {tx('home.cta.heading', 'Ready to Start Your Project?')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
              {tx('home.cta.subtext', "Partner with Koro Living for your next property development. Let's build something exceptional together.")}
            </p>
            <Button asChild size="lg" className="rounded-full px-10 py-6" data-testid="final-cta">
              <Link to="/contact">
                Contact Us Today
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
